import { date } from 'quasar'
import Activity, { SESSION_ADMISSION_TYPES } from 'src/models/activity/Activity'
import ActivitySession from 'src/models/activity-session/ActivitySession'
import Model, { encoders, executeMutation, GqlError, ID, IsoDateTime, LocalizedJsonObject } from 'src/models/model'
import Order, { getAncestorOrder } from 'src/models/order/Order'
import OrderItem, { ORDER_ITEM_TYPES } from 'src/models/order-item/OrderItem'
import activitySessionResolver from 'src/services/activity-session/ActivitySessionResolver'

const { formatDate } = date

export const ACTIVITY_BOOKING_STEPS = {
  TICKETS: 'tickets',
  DATE_AND_TIME: 'date-and-time',
  ADD_ONS: 'add-ons'
}

export const BOOKING_STATES = {
  // Not yet placed.
  DRAFT: 'draft',
  // Placed, not paid, when waitlist is enabled.
  WAITLIST: 'waitlist',
  // Placed, not paid, when waitlist is disabled.
  RESERVATION: 'reservation',
  // Placed, paid.
  BOOKING: 'booking',
  // Some activity tickets voided.
  PARTIALLY_VOIDED: 'partially_voided',
  // All activity tickets voided.
  VOIDED: 'voided'
}

export const ACTIVITY_BOOKING_EDIT_ACTIONS = {
  SWAP: 'swap',
  VOID: 'void',
  EDIT: 'edit'
}

export function formatDateForActivityBookingConfig (date) {
  return date.replace(/-/g, '/')
}

export default class ActivityBooking extends Model {
  static name = 'ActivityBooking'

  static paginate = true

  static define () {
    this.fields = {
      id: {
        type: ID,
        required: true
      },
      state: {
        type: String,
        default: 'draft'
      },
      activity: {
        type: Activity,
        default: null,
        encode: encoders.hasOne,
        reference: true
      },
      order: {
        type: Order,
        reference: true,
        output: true,
        computed () {
          return getAncestorOrder(this) || this.data?.order
        }
      },
      activitySession: {
        type: ActivitySession,
        default: null,
        encode: encoders.hasOne
      },
      activityUnitQuantity: {
        type: Number,
        default: 1
      },
      ticketQuantity: {
        type: Number,
        output: true,
        excludeFromDefault: true
      },
      activitySessionEntryTime: {
        type: IsoDateTime,
        default: null
      },
      activitySessionExitTime: {
        type: IsoDateTime,
        default: null
      },
      config: {
        type: LocalizedJsonObject,
        mergeDefault: true,
        default () {
          return {
            createKey: null,
            participantDetails: [],
            selectedActivitySessionDate: null,
            selectedActivitySpaceId: null
          }
        }
      },
      waitlistFirstName: {
        type: String,
        default: null
      },
      waitlistLastName: {
        type: String,
        default: null
      },
      waitlistPhoneNumber: {
        type: String,
        default: null
      },
      waitlistPhoneNumberCountryCode: {
        type: String,
        default: null
      },
      waitlistEmailAddress: {
        type: String,
        default: null
      },
      waitlistInformationConfirmed: {
        type: Boolean,
        default: false
      },
      canSendWaitlistNotification: {
        type: Boolean,
        output: true,
        excludeFromDefault: true
      },
      waitlistNotificationLastSentAt: {
        type: IsoDateTime,
        output: true,
        excludeFromDefault: true
      },
      notes: {
        type: String,
        default: null
      },
      isActivated: {
        type: Boolean,
        default: false,
        local: true
      }
    }

    this.computed = {
      isDraft () {
        return this.state === BOOKING_STATES.DRAFT
      },
      isWaitlist () {
        return this.state === BOOKING_STATES.WAITLIST
      },
      isVoided () {
        return this.state === BOOKING_STATES.VOIDED
      },
      isForWaitlistEnabledActivity () {
        return this.activity.allowWaitlistActivityBookings
      },
      orderState () {
        return this.order?.state
      },
      activityTicketOrderItems () {
        return _(this.order?.activityTicketOrderItems).filter({ activityTicket: { activityBookingId: this.id } }).value()
      },
      activitySalesItemOrderItems () {
        return _(this.order?.activitySalesItemOrderItems).filter({ activityBookingId: this.id }).value()
      },
      activityOrderItems () {
        return _.concat(this.activityTicketOrderItems, this.activitySalesItemOrderItems)
      },
      activitySalesItemOrderItemsCountByItemVariationId () {
        return _(this.activitySalesItemOrderItems)
          .groupBy(orderItem => orderItem.itemOrderItem.itemVariationId)
          .mapValues(items => items.length)
          .value()
      },
      siblingActivityBookings () {
        return _(this.order?.activityBookings.data).filter(({ id, state }) => id !== this.id && state !== BOOKING_STATES.VOIDED).value()
      },
      siblingShowings () {
        return this.order?.allShowings || []
      },
      itemPackageActivityTicketOrderItems () {
        return _(this.order?.itemPackageActivityTicketOrderItems).filter({ activityTicket: { activityBookingId: this.id } }).value()
      },
      nonItemPackageActivityTicketOrderItems () {
        return _(this.order?.activityTicketOrderItems).filter(orderItem => {
          return _.isNil(orderItem.itemPackageId) && orderItem.activityTicket.activityBookingId === this.id
        }).value()
      },
      numSelectedTickets () {
        return this.activityTicketOrderItems.length
      },
      areMinimumAttendanceRequirementsMet () {
        return this.numSelectedTickets > 0 && (
          this.activity.sessionAdmissionType === SESSION_ADMISSION_TYPES.SESSION ||
          !this.activity.isExclusive ||
          !this.activity.hasMinimumAttendance ||
          this.numSelectedTickets >= this.activity.minimumAttendance
        )
      },
      allowDraftCleanup () {
        return this.isDraft && !this.areMinimumAttendanceRequirementsMet
      },
      guestItemPackages () {
        return this.activity.guestItemPackages || []
      },
      guestTicketTypes () {
        return this.activity.guestTicketTypes || []
      },
      guestTicketTypesOrItemPackages () {
        return _.concat(this.guestTicketTypes, this.guestItemPackages)
      },
      isTicketLimitReached () {
        return this.numSelectedTickets >= this.activity.maxActivitySpaceCapacity
      },
      hasTicketTypesOrItemPackagesSelected () {
        return this.numSelectedTickets > 0
      },
      siteEntryTime () {
        if (_.isNil(this.activitySessionEntryTime)) return this.activitySessionEntryTime

        return this.activitySessionEntryTime.inTimeZone(this.configuration.currentSite?.timeZone)
      },
      siteExitTime () {
        if (_.isNil(this.activitySessionExitTime)) return this.activitySessionExitTime

        return this.activitySessionExitTime.inTimeZone(this.configuration.currentSite?.timeZone)
      },
      durationMinutes () {
        return date.getDateDiff(this.siteExitTime, this.siteEntryTime, 'minutes')
      },
      activityName () {
        return `${this.activity.name} (${this.i18n.t('activityBooking.bookingId', { bookingId: this.id })})`
      },
      activityUnitQuantityDescription () {
        if (this.activity.sessionAdmissionType === SESSION_ADMISSION_TYPES.SESSION || this.activity.maxConsecutiveActivityUnits === 1) {
          return null
        } else {
          return `${this.activityUnitQuantity} ${this.activityUnitQuantity > 1 ? this.activity.activityUnitDescriptionPlural : this.activity.activityUnitDescription}`
        }
      },
      activitySpaceDescription () {
        return this.activitySession?.activitySpace?.name || null
      },
      isComplete () {
        return (
          this.areMinimumAttendanceRequirementsMet &&
          !_.isNil(this.activitySession) &&
          !_.isNil(this.activitySessionEntryTime)
        )
      },
      canBePlaced () {
        return (
          !this.loading &&
          this.isDraft &&
          this.isComplete
        )
      },
      canBeUpdated () {
        return (
          !this.loading &&
          !this.isVoided &&
          this.isComplete
        )
      },
      incompleteErrors () {
        const errors = []

        if (!this.areMinimumAttendanceRequirementsMet) {
          errors.push(this.i18n.t('activityBooking.errors.minimumAttendanceRequirementsNotMet'))
        }

        if (_.isNil(this.activitySession)) {
          errors.push(this.i18n.t('activityBooking.errors.noDateAndTimeSelected'))
        }

        return errors
      },
      hasActivityOverlaps () {
        if (_.isNil(this.activitySessionEntryTime) || _.isNil(this.activitySessionExitTime)) return false

        return _(this.siblingActivityBookings)
          .filter(({ activitySessionEntryTime, activitySessionExitTime }) => !_.isNil(activitySessionEntryTime) && !_.isNil(activitySessionExitTime))
          .some(siblingActivityBooking => (
            (siblingActivityBooking.activitySessionEntryTime.getTime() < this.activitySessionEntryTime.getTime() && siblingActivityBooking.activitySessionExitTime.getTime() >= this.activitySessionEntryTime.getTime()) ||
            (siblingActivityBooking.activitySessionExitTime.getTime() > this.activitySessionExitTime.getTime() && siblingActivityBooking.activitySessionEntryTime.getTime() <= this.activitySessionExitTime.getTime()) ||
            (siblingActivityBooking.activitySessionEntryTime.getTime() >= this.activitySessionEntryTime.getTime() && siblingActivityBooking.activitySessionExitTime.getTime() <= this.activitySessionExitTime.getTime())
          ))
      },
      hasMovieOverlaps () {
        if (_.isNil(this.activitySessionEntryTime) || _.isNil(this.activitySessionExitTime)) return false

        return _(this.siblingShowings)
          .filter(({ time, endTime }) => !_.isNil(time) && !_.isNil(endTime))
          .some(siblingShowing => (
            (siblingShowing.time.getTime() < this.activitySessionEntryTime.getTime() && siblingShowing.endTime.getTime() >= this.activitySessionEntryTime.getTime()) ||
            (siblingShowing.endTime.getTime() > this.activitySessionExitTime.getTime() && siblingShowing.time.getTime() <= this.activitySessionExitTime.getTime()) ||
            (siblingShowing.time.getTime() >= this.activitySessionEntryTime.getTime() && siblingShowing.endTime.getTime() <= this.activitySessionExitTime.getTime())
          ))
      },
      hasOverlaps () {
        if (this.state === BOOKING_STATES.VOIDED) return false

        return this.hasActivityOverlaps || this.hasMovieOverlaps
      },
      bookingUserName () {
        return this.order?.nameForOrder || `${this.order?.user?.firstName ?? ''} ${this.order?.user?.lastName ?? ''}`.trim() || this.i18n.t('activityBooking.guest.name')
      },
      activitySpaceName () {
        if (!_.isNil(this.activitySession?.activitySpace?.name)) {
          return this.activitySession?.activitySpace?.name
        }

        if (this.config?.selectedActivitySpaceId) {
          return this.configuration.activitySpaces.dataById?.[this.config?.selectedActivitySpaceId]?.name
        }
        return this.i18n.t('common.na')
      }
    }

    this.mutations = {
      sendWaitlistNotification: {
        arguments: {
          id: {
            type: ID,
            required: true
          }
        },
        results: {
          success: Boolean,
          errors: [GqlError]
        }
      }
    }
  }

  userTicketTypesOrItemPackages (user) {
    return _(this.activity.memberTicketTypes(user)).concat(this.activity.memberItemPackages(user)).value()
  }

  activate () {
    if (_.isEmpty(this.activityTicketOrderItems)) {
      // If there's only one ticket type or item package available, select it automatically.
      if (this.order.user) {
        const userTicketTypesOrItemPackages = this.userTicketTypesOrItemPackages(this.order.user)

        if (userTicketTypesOrItemPackages.length === 1) {
          this.increment(userTicketTypesOrItemPackages[0], this.order.user)
        }
      } else {
        if (this.guestTicketTypesOrItemPackages.length === 1) {
          this.increment(this.guestTicketTypesOrItemPackages[0])
        }
      }
    }

    this.isActivated = true
  }

  formattedTime (showDate = false, relative = false) {
    if (_.isNil(this.siteEntryTime)) {
      return null
    }

    const today = this.configuration.currentSite.businessDate
    const tomorrow = this.configuration.currentSite.businessTomorrow

    const sessionDate = formatDate(this.siteEntryTime, 'YYYY-MM-DD')

    const entryTime = formatDate(this.siteEntryTime, 'h:mm a')
    const exitTime = formatDate(this.siteExitTime, 'h:mm a')

    if (!showDate) {
      return `${entryTime} until ${exitTime}`
    }

    let date = ''

    if (relative && sessionDate === today) {
      date = this.i18n.t('time.today')
    } else if (relative && sessionDate === tomorrow) {
      date = this.i18n.t('time.tomorrow')
    } else {
      date = formatDate(this.siteEntryTime, 'MMM Do')
    }

    return `${date} from ${entryTime} until ${exitTime}`
  }

  getFromPrice (ticketTypeOrItemPackage) {
    const fromPriceKey = ticketTypeOrItemPackage.typeName === 'ItemPackage' ? 'item_packages' : 'ticket_types'
    return this.activity.fromPrices?.[fromPriceKey]?.[ticketTypeOrItemPackage.id]
  }

  guestTicketTypeOrItemPackageQuantity (ticketTypeOrItemPackage) {
    if (ticketTypeOrItemPackage.typeName === 'ItemPackage') {
      const numTicketTypesInItemPackage = _(ticketTypeOrItemPackage.itemPackageItems.data).filter({ itemType: ORDER_ITEM_TYPES.TICKET }).value().length
      return _(this.itemPackageActivityTicketOrderItems).filter({ itemPackage: { id: ticketTypeOrItemPackage.id }, activityTicket: { user: null } }).value().length / numTicketTypesInItemPackage
    } else {
      return _(this.nonItemPackageActivityTicketOrderItems).filter({ activityTicket: { user: null, ticketType: { id: ticketTypeOrItemPackage.id } } }).value().length
    }
  }

  isGuestTicketTypeOrItemPackageSelected (ticketTypeOrItemPackage) {
    return !!this.guestTicketTypeOrItemPackageQuantity(ticketTypeOrItemPackage)
  }

  isUserTicketTypeOrItemPackageSelected (userOrderData) {
    if (userOrderData.ticketTypeOrItemPackage.typeName === 'ItemPackage') {
      return _.some(this.itemPackageActivityTicketOrderItems, { itemPackage: { id: userOrderData.ticketTypeOrItemPackage.id }, activityTicket: { user: { id: userOrderData.user.id } } })
    } else {
      return _.some(this.nonItemPackageActivityTicketOrderItems, { activityTicket: { user: { id: userOrderData.user.id }, ticketType: { id: userOrderData.ticketTypeOrItemPackage.id } } })
    }
  }

  increment (ticketTypeOrItemPackage, user = null) {
    if (ticketTypeOrItemPackage.typeName === 'ItemPackage') {
      this.order.createItemPackageOrderItemsForActivityAndAddToOrder(ticketTypeOrItemPackage, this.id, { user })
    } else {
      this.order.createActivityTicketOrderItemAndAddToOrder(this.id, ticketTypeOrItemPackage.id, { user })
    }

    this.resetActivitySessionData()
  }

  decrement (ticketTypeOrItemPackage, user = null) {
    if (ticketTypeOrItemPackage.typeName === 'ItemPackage') {
      this.order.removeItemPackageOrderItemsForActivity(ticketTypeOrItemPackage, this.id, { user })
    } else {
      const [lastAddedOrderItem] = _(this.nonItemPackageActivityTicketOrderItems).filter({ activityTicket: { ticketType: { id: ticketTypeOrItemPackage.id }, user } }).reverse().value()

      if (lastAddedOrderItem) {
        this.order.removeOrderItem(lastAddedOrderItem)
      }
    }

    this.resetActivitySessionData()
  }

  incrementActivityUnitQuantity () {
    if (this.activityUnitQuantity < this.activity.maxConsecutiveActivityUnits) {
      this.activityUnitQuantity++
      this.resetActivitySessionData()
    }
  }

  decrementActivityUnitQuantity () {
    if (this.activityUnitQuantity > 1) {
      this.activityUnitQuantity--
      this.resetActivitySessionData()
    }
  }

  incrementSalesItem (itemVariation) {
    const orderItem = new OrderItem({
      type: 'item',
      itemOrderItem: {
        itemId: itemVariation.item.id,
        itemVariationId: itemVariation.id,
        itemModifiers: []
      },
      voucher: null,
      voucherType: null,
      discount: null,
      activityBookingId: this.id
    })

    this.order.orderItems.add(orderItem)
    orderItem.order = this.order
  }

  decrementSalesItem (itemVariation) {
    const [lastAddedOrderItem] = _(this.activitySalesItemOrderItems).filter({ itemOrderItem: { itemVariationId: itemVariation.id } }).reverse().value()

    if (lastAddedOrderItem) {
      this.order.removeOrderItem(lastAddedOrderItem)
    }
  }

  salesItemCount (itemVariation) {
    return this.activitySalesItemOrderItemsCountByItemVariationId[itemVariation.id] || 0
  }

  copyFromSourceActivityBooking (sourceActivityBooking) {
    this.copyActivityTicketsFrom(sourceActivityBooking)
    this.copySalesItemFrom(sourceActivityBooking)
    this.activityUnitQuantity = sourceActivityBooking.activityUnitQuantity
  }

  copySalesItemFrom (swapSourceActivityBooking) {
    const sourceSalesItems = swapSourceActivityBooking.activitySalesItemOrderItems

    sourceSalesItems.forEach((sourceItem) => {
      const itemVariation = sourceItem.itemOrderItem.itemVariation
      if (itemVariation) {
        this.incrementSalesItem(itemVariation)
      }
    })
  }

  copyActivityTicketsFrom (swapSourceActivityBooking) {
    const sourceActivityTickets = swapSourceActivityBooking.activityTicketOrderItems
    sourceActivityTickets.forEach((sourceTicket) => {
      const user = sourceTicket.activityTicket.user

      if (sourceTicket.itemPackageId) {
        const itemPackage = this.order.currentActivity.itemPackages.data.find(
          pkg => pkg.id === sourceTicket.itemPackageId
        )
        if (itemPackage) {
          this.increment(itemPackage, user)
        }
      } else {
        const ticketType = sourceTicket.activityTicket.ticketType
        if (ticketType) {
          this.increment(ticketType, user)
        }
      }
    })
  }

  async selectActivitySessionDate (selectedDate, shouldSaveOrder = true) {
    const selectedDateFormatted = formatDateForActivityBookingConfig(selectedDate)

    if (_.isNil(selectedDate) || selectedDateFormatted === this.config.selectedActivitySessionDate) return

    this.config.selectedActivitySessionDate = selectedDateFormatted
    this.resetActivitySessionData()
    if (shouldSaveOrder) {
      await this.order.save()
    }
  }

  resetActivitySessionData () {
    if (this.config) {
      this.config.selectedActivitySpaceId = null
    }

    this.activitySession = null
    this.activitySessionEntryTime = null
    this.activitySessionExitTime = null
  }

  /**
   * activitySessionsAndAvailableEntries (example):
   * [
   *  {
   *    activitySessionId: 1,
   *    activitySessionEndTime: '2025-01-01T10:00:00Z',
   *    activitySpaceId: 1,
   *    availableEntries: ['2025-01-01T09:00:00Z', '2025-01-01T10:00:00Z', '2025-01-01T11:00:00Z'],
   *    availableEntriesByTimeSlotId: {
   *      1: ['2025-01-01T09:00:00Z', '2025-01-01T10:00:00Z', '2025-01-01T11:00:00Z'],
   *      2: ['2025-01-01T12:00:00Z', '2025-01-01T13:00:00Z', '2025-01-01T14:00:00Z']
   *    }
   *  }
   * ]
   */

  async selectActivitySessionEntryTime (selectedEntryTime, activitySessionsAndAvailableEntries, shouldSaveOrder = true) {
    const { activitySession, activitySessionEntryTime, activitySessionExitTime } = activitySessionResolver.resolve(
      this,
      selectedEntryTime,
      activitySessionsAndAvailableEntries
    )

    this.activitySession = activitySession
    this.activitySessionEntryTime = activitySessionEntryTime
    this.activitySessionExitTime = activitySessionExitTime

    if (shouldSaveOrder) {
      await this.order.save()
    }
  }

  async toggleSelectedActivitySpace (activitySpace, activitySessionsAndAvailableEntries, shouldSaveOrder = true) {
    if (this.config.selectedActivitySpaceId === activitySpace.id) {
      this.config.selectedActivitySpaceId = null
    } else {
      this.config.selectedActivitySpaceId = activitySpace.id
    }

    const { activitySession, activitySessionEntryTime, activitySessionExitTime } = activitySessionResolver.resolve(
      this,
      this.activitySessionEntryTime,
      activitySessionsAndAvailableEntries
    )

    this.activitySession = activitySession
    this.activitySessionEntryTime = activitySessionEntryTime
    this.activitySessionExitTime = activitySessionExitTime

    if (shouldSaveOrder) {
      await this.order.save()
    }
  }

  isEntryTimeAvailable (entryTime, availableEntries) {
    return _(availableEntries).map(entry => new IsoDateTime(entry).toString()).includes(entryTime.toString())
  }

  isEntryTimeSelected (entryTime) {
    return this.activitySessionEntryTime?.toString() === entryTime.toString()
  }

  isActivitySpaceSelected (activitySpace) {
    if (!_.isNil(this.activitySession?.activitySpace)) {
      return this.activitySession.activitySpace.id === activitySpace.id
    } else {
      return this.config.selectedActivitySpaceId === activitySpace.id
    }
  }

  isEntryTimeDisabled (entryTime, activitySessionsAndAvailableEntries) {
    if (_.isNil(this.config.selectedActivitySpaceId)) return false

    const activitySessionsAndAvailableEntriesFiltered = this.filterActivitySessionsAndAvailableEntries(
      activitySessionsAndAvailableEntries,
      { activitySpaceId: this.config.selectedActivitySpaceId }
    )

    const allAvailableEntryTimesForActivitySpace = this.allAvailableEntryTimes(activitySessionsAndAvailableEntriesFiltered)

    return !this.isEntryTimeAvailable(entryTime, allAvailableEntryTimesForActivitySpace)
  }

  isActivitySpaceDisabled (activitySpace, activitySessionsAndAvailableEntries) {
    if (_.isNil(this.activitySessionEntryTime)) return false

    const activitySessionsAndAvailableEntriesFiltered = this.filterActivitySessionsAndAvailableEntries(
      activitySessionsAndAvailableEntries,
      { activitySpaceId: activitySpace.id }
    )

    const allAvailableEntryTimesForActivitySpace = this.allAvailableEntryTimes(activitySessionsAndAvailableEntriesFiltered)

    return !this.isEntryTimeAvailable(this.activitySessionEntryTime, allAvailableEntryTimesForActivitySpace)
  }

  filterActivitySessionsAndAvailableEntries (activitySessionsAndAvailableEntries, filterOptions = {}) {
    return !_.isEmpty(filterOptions)
      ? _(activitySessionsAndAvailableEntries).filter(filterOptions).value()
      : activitySessionsAndAvailableEntries
  }

  allAvailableEntryTimes (activitySessionsAndAvailableEntries, timeSlotId = null) {
    return _(activitySessionsAndAvailableEntries)
      .map(activitySessionObject => {
        if (!_.isNil(timeSlotId)) {
          return activitySessionObject.availableEntriesByTimeSlotId[timeSlotId]
        } else {
          return activitySessionObject.availableEntries
        }
      })
      .flatten()
      .compact()
      .map(dateString => new IsoDateTime(dateString))
      .uniqBy(dateTime => dateTime.i18n('shortTime'))
      .sortBy(date => date.getTime())
      .value()
  }

  timeSlotOptions (activitySessionsAndAvailableEntries) {
    const timeSlotIds = _(activitySessionsAndAvailableEntries)
      .map(activitySessionObject => _.keys(activitySessionObject.availableEntriesByTimeSlotId))
      .flatten()
      .uniq()
      .value()

    return _.filter(this.configuration.timeSlots.data, timeSlot => _.includes(timeSlotIds, timeSlot.id.toString()))
  }

  entryTimeOptions (activitySessionsAndAvailableEntries, timeSlotId = null) {
    return _(this.allAvailableEntryTimes(activitySessionsAndAvailableEntries, timeSlotId))
      .map(dateTime => {
        // Convert to ISO string format to match availableEntries format
        const timeString = dateTime.toISOString()
        const isDisabled = this.isEntryTimeDisabled(dateTime, activitySessionsAndAvailableEntries)

        let label = dateTime.inTimeZone(this.configuration.currentSite.timeZone).i18n('shortTime')

        // Add space information to the label only if the time is disabled
        if (isDisabled) {
          // Find the first session that has this time available
          const firstAvailableSession = _.find(activitySessionsAndAvailableEntries, activitySession =>
            activitySession.availableEntries && _.includes(activitySession.availableEntries, timeString)
          )

          if (firstAvailableSession) {
            // Find the space name for this session
            const activitySpaceName = this.configuration.activitySpaces.dataById[firstAvailableSession.activitySpaceId].name

            if (activitySpaceName) {
              label += ` (${activitySpaceName})`
            }
          }
        }

        return {
          label,
          value: dateTime,
          disable: isDisabled
        }
      })
      .value()
  }

  allActivitySpaces (activitySessionsAndAvailableEntries) {
    const activitySpaceIdsFromActivitySessions = _(activitySessionsAndAvailableEntries).map('activitySpaceId').uniq().value()
    return _(this.configuration.activitySpaces.data).filter(({ id }) => activitySpaceIdsFromActivitySessions.includes(id)).uniqBy('id').value()
  }

  prefillWaitlistInfoFromOrderUser (fields = []) {
    const prefillAllFields = _.isEmpty(fields)

    if (_.isNil(this.order.user)) return

    if (prefillAllFields || _.includes(fields, 'waitlistFirstName')) {
      this.waitlistFirstName = this.order.user.firstName
    }

    if (prefillAllFields || _.includes(fields, 'waitlistLastName')) {
      this.waitlistLastName = this.order.user.lastName
    }

    if (prefillAllFields || _.includes(fields, 'waitlistPhoneNumber')) {
      this.waitlistPhoneNumber = this.order.user.phone
    }

    if (prefillAllFields || _.includes(fields, 'waitlistPhoneNumberCountryCode')) {
      this.waitlistPhoneNumberCountryCode = this.order.user.phoneCountryCode
    }

    if (prefillAllFields || _.includes(fields, 'waitlistEmailAddress')) {
      this.waitlistEmailAddress = this.order.user.email
    }
  }

  async sendWaitlistNotification () {
    const results = await executeMutation('sendWaitlistNotification', { id: this.id }, { success: true })

    return results
  }
}
